import { Id } from 'types/definitions'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { csvDownload } from 'utils/csv-util'
import { isNotNil } from 'utils/object-util'
import Job from 'jobs/Job'
import Document from 'documents/Document'
import { JobDocumentsAdapter } from 'jobs/JobDocumentsViewModelAdapter'
import WorkpapersViewModel from './WorkpapersViewModel'
import { ViewModelList } from 'infra/ViewModelList'
import TaxPreparationStore from 'jobs/TaxPreparationStore'
import { AlertFactory } from 'alerts/Alert'

export default class WorkpapersRootViewModel {
  isMenuOpen = false

  selectedWorkpaper: WorkpapersViewModel | null = null

  private readonly workpapersVMs

  constructor(
    public job: Job,
    adapter: JobDocumentsAdapter,
    private taxPreparationStore: TaxPreparationStore,
    private newAlert: AlertFactory,
  ) {
    makeObservable(this, {
      selectedWorkpaper: observable,
      isMenuOpen: observable,
      workpapersCount: computed,
      validatedWorkpapersCount: computed,
      workpaper: computed,
      workpapers: computed,
      edit: action.bound,
      cycleWorkpaper: action.bound,
      toggleMenu: action.bound,
    })

    this.workpapersVMs = new ViewModelList<Document, WorkpapersViewModel>({
      create: (model) =>
        new WorkpapersViewModel(model, adapter.documentActionsAdapter),
      models: () =>
        adapter
          .getJobDocuments(Id.assert(job.id))
          .filter((doc) => !doc.archived && isNotNil(doc.extractionState)),
      activate: (vm) => vm.activate(),
      deactivate: (vm) => vm.deactivate(),
    })
  }

  get validatedWorkpapersCount() {
    return this.workpapersVMs.items.filter((vm) => vm.isValidated).length
  }

  get workpapersCount() {
    return this.workpapersVMs.items.length
  }

  get workpaper() {
    if (!this.workpapersVMs.items.length) {
      return
    }

    return this.selectedWorkpaper || this.workpapersVMs.items[0]
  }

  get workpapers() {
    return this.workpapersVMs.items
  }

  activate() {
    this.workpapersVMs.activate()
  }

  deactivate() {
    this.workpapersVMs.deactivate()
    this.selectedWorkpaper = null
  }

  edit(workpaper: WorkpapersViewModel) {
    this.selectedWorkpaper = workpaper
  }

  cycleWorkpaper(moveForward: boolean) {
    const currentIndex = this.workpapersVMs.items.findIndex(
      (vm) => vm.id === this.workpaper?.id,
    )

    const desiredIndex = moveForward
      ? (currentIndex + 1) % this.workpapersCount
      : (this.workpapersCount + currentIndex - 1) % this.workpapersCount

    this.edit(this.workpapersVMs.items[desiredIndex])
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen
  }

  downloadCsv = task(() => {
    const header = ['Document', 'Type', 'Name', 'Field', 'Value', 'Corrected']

    const csvData = this.workpapers
      .map((workpaper) => workpaper.toCsvRows())
      .flat()

    csvDownload([header, ...csvData], 'document-fields.csv')
  })

  drakeDownload = task(async () => {
    await this.taxPreparationStore
      .downloadDrakeTaxReturn(this.job)
      .catch((err) => this.newAlert().fromError(err).show())
  })
}
